import "./style.scss";

import { useCallback, useEffect, useRef, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import { icons } from "./icons";

interface ControlsProps {
  isAvatarSpeaking?: boolean;
  isMuted: boolean;
  selectedMicrophone?: MediaDeviceInfo | null;
  toggleMute?: () => void;
  handleEnd?: () => void;
  onStopTalking?: () => void;
}

export function Controls({
  isAvatarSpeaking,
  isMuted,
  toggleMute,
  selectedMicrophone,
  handleEnd,
  onStopTalking,
}: ControlsProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const barArrayRef = useRef<number[]>([]); // Store the bar data
  const audioDevicesRef = useRef<MediaStream>();

  const [isUserTalking, setIsUserTalking] = useState(false);

  const connectAudio = useCallback(async () => {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const audioInputDevices = devices?.filter(
      device => device.kind === "audioinput",
    );

    if (audioInputDevices?.length > 0) {
      audioDevicesRef.current = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });

      const audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(
        audioDevicesRef.current,
      );
      source.connect(analyser);
      analyserRef.current = analyser;
      drawMicrophoneWaveform();
    }
  }, [audioDevicesRef.current, selectedMicrophone]);

  useEffect(() => {
    void connectAudio();
    return () => {
      if (audioDevicesRef.current) {
        audioDevicesRef.current.getTracks().forEach(track => track.stop());
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [connectAudio, selectedMicrophone]);

  const drawMicrophoneWaveform = () => {
    if (!canvasRef.current || !analyserRef.current) return;

    const canvas = canvasRef.current;
    const canvasCtx = canvas.getContext("2d");
    const analyser = analyserRef.current;

    if (!canvasCtx) return;

    analyser.fftSize = 256;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const barWidth = 2; // Width of each bar
    const gap = 1; // Gap between bars
    const totalBars = Math.floor(canvas.width / (barWidth + gap)); // How many bars fit on the screen

    // Fill the bar array with initial zeros
    if (barArrayRef.current.length === 0) {
      barArrayRef.current = new Array(totalBars).fill(0);
    }

    let talkingCooldown = 0;
    const cooldownFrames = 30; // About 0.5 seconds at 60fps

    const draw = () => {
      // Clear the canvas for each frame
      canvasCtx.clearRect(0, 0, canvas.width, canvas.height);

      if (!isMuted) {
        analyser.getByteFrequencyData(dataArray);

        const talkingThreshold = 6;
        const requiredFrames = 3;

        // Calculate average amplitude
        const averageAmplitude =
          dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
        const barHeight = (averageAmplitude / 255) * canvas.height * 0.8;

        // Shift existing bars to the left
        barArrayRef.current.shift();
        barArrayRef.current.push(barHeight);

        // Count talking frames
        const talkingFrames = barArrayRef.current.filter(
          height => height > talkingThreshold,
        ).length;

        let isUserTalkingNow = talkingFrames >= requiredFrames;

        // Update cooldown
        if (isUserTalkingNow) {
          talkingCooldown = cooldownFrames;
        } else if (talkingCooldown > 0) {
          talkingCooldown--;
          isUserTalkingNow = true; // Keep it true during cooldown
        }

        // Update talking state
        setIsUserTalking(isUserTalkingNow);

        // Set color for the entire waveform
        canvasCtx.fillStyle = "#ffffff";

        // Draw all bars
        for (let i = 0; i < barArrayRef.current.length; i++) {
          const x = i * (barWidth + gap);
          const height = barArrayRef.current[i];

          // Draw the bar on both top and bottom halves
          canvasCtx.fillRect(
            x,
            canvas.height / 2 - height / 2,
            barWidth,
            height,
          );
          canvasCtx.fillRect(
            x,
            canvas.height / 2 + height / 2,
            barWidth,
            -height,
          );
        }
      } else {
        // If muted, draw a flat line
        canvasCtx.beginPath();
        canvasCtx.moveTo(0, canvas.height / 2);
        canvasCtx.lineTo(canvas.width, canvas.height / 2);
        canvasCtx.strokeStyle = "#ffffff";
        canvasCtx.stroke();

        // Reset the user talking state when muted
        setIsUserTalking(false);

        // Reset the bar array to zeros when muted
        barArrayRef.current.fill(0);
      }

      animationFrameRef.current = requestAnimationFrame(draw);
    };

    draw();
  };

  // Determine the filter style based on talking state and muted state
  const waveformStyle = {
    filter:
      !isMuted && isUserTalking
        ? "brightness(0) saturate(100%) invert(48%) sepia(93%) saturate(1720%) hue-rotate(346deg) brightness(101%) contrast(103%)"
        : "none",
    transition: "filter 0.3s ease",
    height: "40px",
  };

  return (
    <div className="d-flex align-items-center gap-3 controls-container">
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id="tooltip-top">{isMuted ? "Unmute" : "Mute"}</Tooltip>
        }
      >
        <div onClick={toggleMute} className="mic-container">
          {isMuted ? icons.muted : icons.unmuted}
        </div>
      </OverlayTrigger>
      <div style={waveformStyle}>
        <canvas
          ref={canvasRef}
          width={120}
          height={40}
          className="waveform-canvas"
        />
      </div>
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip id="tooltip-top">Stop</Tooltip>}
      >
        <div
          onClick={onStopTalking}
          className={`mic-container${isAvatarSpeaking ? "" : " d-none"}`}
        >
          {icons.stop}
        </div>
      </OverlayTrigger>
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip id="tooltip-top">End Session</Tooltip>}
      >
        <div onClick={handleEnd} className="btn-container">
          {icons.cross}
        </div>
      </OverlayTrigger>
    </div>
  );
}
